import { Grid, Paper } from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import PlanHeader from "../../../shared/enrollment/planCode/PlanHeader";
import DoneOutlineIcon from "@mui/icons-material/DoneOutline";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import EditIcon from "@mui/icons-material/Edit";
import { useContext, useCallback, useMemo } from "react";
import downloadFile from "../../../../utils/commonFunctions/downloadFile";
import { useLocation } from "react-router-dom";
import { ThemeContext } from "../../../../contexts";
import { AcknowledgementProps } from "../../../../types/components/enrollmentProps.types";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import PanToolIcon from "@mui/icons-material/PanTool";
import ClearIcon from "@mui/icons-material/Clear";
import PhoneIcon from "@mui/icons-material/Phone";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
	CancerBrochure,
	WholeLifeBrochure,
	CriticalIllnessBrochure,
	KemperShortTermBrochure,
	KemperAccidentBrochure,
	HospitalndemnityBrochure,
	GroupLifeIndemnity,
	FamilyProtection,
	DocRx,
	Vision,
	DentalPlan
} from "../../../../assets";
import "./enrollmentAcknowledgement.css";
import { Status } from "../../../../types/enrollment.types";
import { dateTimeZoneConverter, getFirstOfNextMonth } from "../../../../utils/commonFunctions/date";
import VEBPlanHeader from "../../../shared/enrollment/VEBPlanHeader/PlanHeader";
import { VEBPlanCodes2024 } from "../../../../constants/VEBPlanCodes/VEBPlanCodes2024";
import { VEBYearlyOpenAndCurrentEffectiveDate } from "../../../../constants/VEBYearlyOpenAndCurrentDate";

type StatusIcon = {
	[key in Status]: JSX.Element;
};

type StatusMessage = {
	[key in Status]: string;
};

const statusIcon: StatusIcon = {
	ACTIVE: <DoneAllIcon className="thank-icon" />,
	SUBMITTED: <DoneOutlineIcon className="thank-icon" />,
	REJECTED: <ClearIcon className="thank-icon" />,
	WAIVED: <PanToolIcon className="thank-icon" />,
	OPENED: <></>,
	CLOSED: <></>,
	"WAIVED - WAITING FOR APPROVAL": <></>
};

const statusMessage: StatusMessage = {
	ACTIVE: "Your enrollment is successfully Activated",
	SUBMITTED: "Your enrollment has been successfully Submitted",
	REJECTED: "Your enrollment has been Rejected",
	WAIVED: "You enrollment is successfully waived",
	OPENED: "",
	CLOSED: "",
	"WAIVED - WAITING FOR APPROVAL": ""
};

const EnrollmentAcknowledgement = ({
	idCardAccessPointRequired,
	dependentChangePointRequired,
	carrierPhoneNo,
	carrierWebsite,
	planDocument,
	planName,
	addDependentLink,
	removeDependentLink,
	isYearlyOpen,
	planCode,
	status
}: AcknowledgementProps): JSX.Element => {
	const { theme } = useContext(ThemeContext);
	const location = useLocation();
	const urlSearchParams: URLSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
	// console.log("status", status);

	const handlePlanBrochureDownload = useCallback(() => {
		const step = urlSearchParams.get("step");
		switch (step) {
			case "Cancer":
				downloadFile(CancerBrochure, "MedMutual_Cancer_Plan_Brochure");
				break;
			case "Whole life":
				downloadFile(WholeLifeBrochure, "MedMutual_Whole_Life_Plan_Brochure");
				break;
			case "Critical Illness":
				downloadFile(CriticalIllnessBrochure, "MedMutual_Critical_Illness_Plan_Brochure");
				break;
			case "Short Term Disability":
				downloadFile(KemperShortTermBrochure, "MedMutual_Short_Term_Brochure");
				break;
			case "Accident":
				downloadFile(KemperAccidentBrochure, "MedMutual_Accident_Plan_Brochure");
				break;
			case "Hospital Indemnity":
				downloadFile(HospitalndemnityBrochure, "MedMutual_Hospital_Indemnity_Plan_Brochure");
				break;
			case "Group Life Indemnity":
				downloadFile(GroupLifeIndemnity, "Group_Life_Indemnity_Plan_Brochure");
				break;
			case "Family Protection":
				downloadFile(FamilyProtection, "Family_Protection_Plan_Brochure");
				break;
			case "Doc and Rx":
				downloadFile(DocRx, "Doc_&_Rx_Plan_Brochure");
				break;
			case "Vision":
				downloadFile(Vision, "Vision_Plan_Brochure");
				break;
			case "Dental":
				downloadFile(DentalPlan, "Dental_Plan_Brochure_All");
				// downloadFile(DentalPlan2, "Dental Plan Brochure");
				// downloadFile(DentalPlan3, "Dental Plan Brochure");
				break;
		}
	}, [urlSearchParams]);

	// console.log("planme------------>222", planName);

	return (
		<div className="enrollment-acknowledgement">
			<VEBPlanHeader
				effectiveDate={
					isYearlyOpen === "YEARLY"
						? VEBYearlyOpenAndCurrentEffectiveDate.veb_yearly_open_effective_date_YYYYMMDD
						: VEBYearlyOpenAndCurrentEffectiveDate.veb_current_effective_date_MMDDYYYY || "N/A"
				}
				planName={planName}
				planCode={planCode ? (planCode as unknown as string) : "No Plan is Opened"}
			/>
			<div className="theme-plan-section-margin" />
			{!["OPENED", "CLOSED"].includes(status) ? (
				<>
					<Grid container direction="column" alignItems="center">
						{statusIcon[status as keyof StatusIcon]}
					</Grid>
					<div className="thank-note">{statusMessage[status as keyof StatusMessage]}</div>
				</>
			) : null}
			<div className="theme-plan-section-margin" />

			{/* ── Benefit Confirmation Content Box ── */}
			<div
				style={{
					border: `1.5px solid ${theme.primary_color}22`,
					borderLeft: `4px solid ${theme.primary_color}`,
					borderRadius: "10px",
					backgroundColor: `${theme.primary_color}08`,
					padding: "20px 24px",
					marginBottom: "16px"
				}}
			>
				{/* Success message */}
				<div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "14px" }}>
					<CheckCircleOutlineIcon style={{ color: theme.primary_color, marginTop: "2px", flexShrink: 0 }} />
					<div>
						<p style={{ margin: 0, fontWeight: 700, fontSize: "15px", color: "#1a1a2e" }}>
							Your benefit selection has been successfully processed.
						</p>
						<p style={{ margin: "4px 0 0", fontSize: "13.5px", color: "#444" }}>
							You can review your plan details card from your <strong>Benefit Section</strong> at any
							time.
						</p>
						<p style={{ margin: "4px 0 0", fontSize: "13.5px", color: "#444" }}>
							For updates or support, please visit your portal or contact your benefits administrator.
						</p>
					</div>
				</div>

				{/* Divider */}
				<div style={{ borderTop: `1px dashed ${theme.primary_color}33`, margin: "14px 0" }} />

				{/* Contact heading */}
				<p
					style={{
						margin: "0 0 12px",
						fontWeight: 700,
						fontSize: "13px",
						color: theme.primary_color,
						textTransform: "uppercase",
						letterSpacing: "0.6px"
					}}
				>
					For Portal-related Queries
				</p>

				{/* Contacts grid */}
				<div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
					{/* Contact card – Becca */}
					<div
						style={{
							flex: "1 1 180px",
							background: "#fff",
							border: `1px solid ${theme.primary_color}22`,
							borderRadius: "8px",
							padding: "12px 14px"
						}}
					>
						<p style={{ margin: "0 0 2px", fontWeight: 700, fontSize: "13.5px", color: "#1a1a2e" }}>
							Becca Boland
						</p>
						<p style={{ margin: "0 0 6px", fontSize: "12px", color: theme.primary_color, fontWeight: 600 }}>
							Human Resources
						</p>
						<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
							<PhoneIcon style={{ fontSize: "14px", color: "#888" }} />
							<span style={{ fontSize: "13px", color: "#444" }}>(936) 525-0145</span>
						</div>
					</div>

					{/* Contact card – Jennifer */}
					<div
						style={{
							flex: "1 1 180px",
							background: "#fff",
							border: `1px solid ${theme.primary_color}22`,
							borderRadius: "8px",
							padding: "12px 14px"
						}}
					>
						<p style={{ margin: "0 0 2px", fontWeight: 700, fontSize: "13.5px", color: "#1a1a2e" }}>
							Jennifer Robertson
						</p>
						<p style={{ margin: "0 0 6px", fontSize: "12px", color: theme.primary_color, fontWeight: 600 }}>
							Benefits
						</p>
						<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
							<PhoneIcon style={{ fontSize: "14px", color: "#888" }} />
							<span style={{ fontSize: "13px", color: "#444" }}>(936) 634-8155 ext 4212</span>
						</div>
					</div>

					{/* Contact card – Call Center */}
					<div
						style={{
							flex: "1 1 180px",
							background: "#fff",
							border: `1px solid ${theme.primary_color}22`,
							borderRadius: "8px",
							padding: "12px 14px"
						}}
					>
						<p style={{ margin: "0 0 2px", fontWeight: 700, fontSize: "13.5px", color: "#1a1a2e" }}>
							Benefits Call Center
						</p>
						<p style={{ margin: "0 0 6px", fontSize: "12px", color: theme.primary_color, fontWeight: 600 }}>
							General Support
						</p>
						<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
							<PhoneIcon style={{ fontSize: "14px", color: "#888" }} />
							<span style={{ fontSize: "13px", color: "#444" }}>833-356-0852</span>
						</div>
					</div>
				</div>
			</div>

			{/* ── Plan contact row (existing) ── */}
		</div>
	);
};

export default EnrollmentAcknowledgement;
